import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Tabs,
  Tab,
  Popover,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors } from "./source.styles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SourceIcon from "@mui/icons-material/Source";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaymentIcon from "@mui/icons-material/Payment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enIN } from "date-fns/locale";
import dayjs from "dayjs";

const TABS = [
  {
    label: "Sources",
    icon: <SourceIcon sx={{ fontSize: 18 }} />,
    value: "sources",
  },
  {
    label: "Services",
    icon: <MiscellaneousServicesOutlinedIcon sx={{ fontSize: 18 }} />,
    value: "services",
  },
  {
    label: "Conversions",
    icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
    value: "conversions",
  },
  {
    label: "Payments",
    icon: <PaymentIcon sx={{ fontSize: 18 }} />,
    value: "payments",
  },
  {
    label: "Bookings",
    icon: <EventAvailableIcon sx={{ fontSize: 18 }} />,
    value: "bookings",
  },
  {
    label: "Employee Reports",
    icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} />,
    value: "employee",
  },
];

const SourceHeader = ({
  activeTab = "sources",
  onTabChange,
  onExport,
  dateRange,
  onDateChange,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const primaryDarkColor = theme.palette.primary.dark;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleDateClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatDateRange = () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      return "Select Date Range";
    }
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  };

  const selection = {
    startDate: dateRange?.startDate || new Date(),
    endDate: dateRange?.endDate || new Date(),
    key: "selection",
  };

  return (
    <Box
      sx={{ bgcolor: "#FFFFFF", borderBottom: `1px solid ${colors.border}` }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 4, pt: 3, pb: 2 }}
      >
        {/* Title */}
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Business Intelligence & Reports
        </Typography>

        {/* Actions */}
        <Stack direction="row" spacing={2}>
          {/* Date Range */}
          <Button
            variant="outlined"
            startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleDateClick}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              borderColor: open ? primaryColor : colors.border,
              color: open ? primaryColor : colors.textSecondary,
              "&:hover": {
                borderColor: primaryColor,
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            {formatDateRange()}
          </Button>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 3,
                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
              },
            }}
          >
            <DateRange
              ranges={[selection]}
              locale={enIN}
              moveRangeOnFirstSelection={false}
              showMonthAndYearPickers={true}
              rangeColors={[primaryColor]}
              onChange={({ selection }) => {
                let startDate = selection.startDate;
                let endDate = selection.endDate || selection.startDate;

                // If single date selected (same day), set endDate to end of that day
                if (startDate.toDateString() === endDate.toDateString()) {
                  startDate = new Date(startDate.setHours(0, 0, 0, 0));
                  endDate = new Date(endDate);
                  endDate.setHours(23, 59, 59, 999);
                }

                onDateChange?.({
                  startDate,
                  endDate,
                });
              }}
            />
          </Popover>

          {/* Export */}
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onExport}
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              bgcolor: primaryColor,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                bgcolor: primaryDarkColor,
                boxShadow: "none",
              },
            }}
          >
            Export
          </Button>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => onTabChange?.(newValue)}
        sx={{
          px: 4,
          minHeight: 48,
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
            bgcolor: primaryColor,
          },
        }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            icon={tab.icon}
            iconPosition="start"
            label={tab.label}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              minHeight: 48,
              color: colors.textMuted,
              "&.Mui-selected": {
                color: primaryColor,
              },
              "& .MuiTab-iconWrapper": {
                mr: 1,
              },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default SourceHeader;
